import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const CART_KEY = 'uf_cart';

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem(CART_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  // Sync state → localStorage
  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  // Listen for changes from other tabs
  useEffect(() => {
    const syncCart = (e) => {
      if (e.key === CART_KEY) {
        const parsed = e.newValue ? JSON.parse(e.newValue) : [];
        setCart(parsed);
      }
    };
    window.addEventListener('storage', syncCart);
    return () => window.removeEventListener('storage', syncCart);
  }, []);

  const addToCart = useCallback((product, quantity = 1, size = null) => {
    setCart(prev => {
      const pid = product.product_id || product.id;
      const existing = prev.find(item => (item.product_id || item.id) === pid && item.size === size);
      if (existing) {
        return prev.map(item =>
          (item.product_id || item.id) === pid && item.size === size
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { ...product, id: pid, product_id: pid, quantity, size }];
    });
  }, []);

  const removeFromCart = useCallback((productId, size = null) => {
    setCart(prev => prev.filter(item => {
      const itemId = item.product_id || item.id;
      const itemSize = item.size || null;
      const targetSize = size || null;
      return !(itemId === productId && itemSize === targetSize);
    }));
  }, []);

  const updateQuantity = useCallback((productId, size, quantity) => {
    const targetSize = size || null;
    if (quantity <= 0) { removeFromCart(productId, targetSize); return; }
    setCart(prev => prev.map(item => {
      const itemId = item.product_id || item.id;
      const itemSize = item.size || null;
      return (itemId === productId && itemSize === targetSize) ? { ...item, quantity } : item;
    }));
  }, [removeFromCart]);

  const clearCart = useCallback(() => setCart([]), []);
  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart, cartTotal, cartCount }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
